import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Breadcrumbs, Link, Paper, Skeleton, Alert, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { trpc } from '../../lib/trpc';
import { ChannelForm } from '../../components/notifications';
import type { ChannelSubmitData, ChannelInitialData } from '../../components/notifications';
import type { ChannelType } from '../../components/notifications';

/**
 * Loading skeleton for the edit form
 */
function EditFormSkeleton() {
  return (
    <Paper sx={{ p: 4, maxWidth: 600 }}>
      <Skeleton variant="text" width="40%" height={32} sx={{ mb: 3 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={56} />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
        <Skeleton variant="rounded" width={100} height={36} />
        <Skeleton variant="rounded" width={120} height={36} />
      </Box>
    </Paper>
  );
}

/**
 * Not found state component
 */
function NotFoundState() {
  return (
    <Paper sx={{ p: 6, textAlign: 'center', maxWidth: 500, mx: 'auto' }}>
      <ErrorOutlineIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
        Channel not found
      </Typography>
      <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
        The notification channel you're looking for doesn't exist or has been deleted.
      </Typography>
      <Button component={RouterLink} to="/notifications" variant="contained">
        Back to Channels
      </Button>
    </Paper>
  );
}

/**
 * Edit Channel page component
 * Allows users to edit an existing notification channel
 */
export function EditChannelPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const utils = trpc.useUtils();

  // Fetch channel data
  const {
    data: channel,
    isLoading,
    error,
  } = trpc.notifications.getById.useQuery({ id: id! }, { enabled: !!id });

  // Update mutation
  const updateMutation = trpc.notifications.update.useMutation({
    onSuccess: () => {
      // Invalidate queries to refresh data
      utils.notifications.list.invalidate();
      utils.notifications.getById.invalidate({ id: id! });

      navigate('/notifications');
    },
  });

  const handleSubmit = (data: ChannelSubmitData) => {
    if (!id) return;

    updateMutation.mutate({
      id,
      data: {
        name: data.name,
        config: data.config,
      },
    });
  };

  const handleCancel = () => {
    navigate('/notifications');
  };

  // Transform API data to form initial data
  const initialData: ChannelInitialData | undefined = channel
    ? {
        id: channel.id,
        name: channel.name,
        type: channel.type as ChannelType,
        config: channel.config as ChannelInitialData['config'],
      }
    : undefined;

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/notifications" color="inherit" underline="hover">
          Notification Channels
        </Link>
        <Typography color="text.primary">
          {isLoading ? 'Loading...' : channel?.name || 'Edit Channel'}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
          {isLoading ? <Skeleton width={200} /> : `Edit ${channel?.name || 'Channel'}`}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Update your notification channel settings
        </Typography>
      </Box>

      {/* Error state */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error.message}
        </Alert>
      )}

      {/* Content */}
      {isLoading ? (
        <EditFormSkeleton />
      ) : !channel ? (
        <NotFoundState />
      ) : initialData ? (
        <ChannelForm
          mode="edit"
          initialData={initialData}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={updateMutation.isLoading}
          error={updateMutation.error?.message ?? null}
        />
      ) : null}
    </Box>
  );
}

export default EditChannelPage;
